import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getScouting, listClubTeams, listMatches, me } from '../api/portal';

export function ScoutingPage() {
  const profile = useQuery({ queryKey: ['portal-me'], queryFn: me });
  const teams = useQuery({ queryKey: ['portal-club-teams'], queryFn: listClubTeams });
  const matches = useQuery({ queryKey: ['portal-matches'], queryFn: listMatches });
  const nextOpp = useMemo(() => {
    const upcoming = (matches.data ?? [])
      .filter((m) => m.status === 'scheduled' || m.status.startsWith('in_play'))
      .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime())[0];
    return upcoming ?? null;
  }, [matches.data]);
  const suggestedId = useMemo(() => {
    if (!nextOpp || !teams.data?.length) return '';
    const own = new Set((profile.data?.allowed_teams ?? []).map((t) => t.name));
    const oppName = [nextOpp.home, nextOpp.away].find((n) => n && !own.has(n)) ?? nextOpp.away;
    return teams.data.find((t) => t.name === oppName)?.id ?? '';
  }, [nextOpp, teams.data, profile.data]);
  const [teamId, setTeamId] = useState('');
  const chosen = teamId || suggestedId || (teams.data?.[0]?.id ?? '');
  const report = useQuery({
    queryKey: ['portal-scouting', chosen],
    queryFn: () => getScouting(chosen),
    enabled: !!chosen,
  });
  const r = report.data;

  return (
    <div>
      <div className="page-head">
        <div className="page-kicker">Scouting</div>
        <h1 className="page-title">Trener pack</h1>
        <p className="page-sub">
          Poslednjih 5: shot-map plus playlist 7m / 9m / 2 min — ne samo tačke. Klubovi to naručuju od statističara.
        </p>
      </div>
      <div className="mb-4 max-w-md">
        <label className="label">Protivnik</label>
        <select className="input" value={chosen} onChange={(e) => setTeamId(e.target.value)}>
          <option value="">Izaberi tim</option>
          {(teams.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        {nextOpp && (
          <p className="mt-1 text-[12px] text-slate-500">
            Sledeći meč: {nextOpp.home} vs {nextOpp.away}
          </p>
        )}
      </div>
      {r && (
        <>
          <h2 className="mb-3 text-lg font-semibold text-white">{r.team_name}</h2>
          <div className="grid gap-3 md:grid-cols-4 mb-4">
            <Stat label="7m %" value={`${r.seven_meter_pct.toFixed(0)}%`} hint={`${r.seven_meter_goals}/${r.seven_meter_attempts}`} />
            <Stat label="2 min" value={String(r.two_minute)} />
            <Stat label="9m šutevi" value={String(r.nine_meter)} />
            <Stat label="Utakmica" value={String(r.matches.length)} hint="poslednjih 5 FT" />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="card p-4">
              <div className="text-[11px] uppercase text-slate-500 mb-2">Poslednjih 5</div>
              {r.matches.map((m) => (
                <div key={m.id} className="flex justify-between py-1 text-sm">
                  <Link to={`/matches/${m.id}/report`} className="text-white hover:underline">{m.home} vs {m.away}</Link>
                  <span className="font-mono text-slate-300">{m.score_home}–{m.score_away}</span>
                </div>
              ))}
              {r.matches.length === 0 && <div className="text-sm text-slate-500">Nema FT uzorka.</div>}
            </div>
            <div className="card p-4">
              <div className="text-[11px] uppercase text-slate-500 mb-2">Ko šutira sa 9m</div>
              {r.nine_meter_shooters.map((p) => (
                <div key={p.player} className="flex justify-between py-1 text-sm">
                  <span className="text-white">{p.player}</span>
                  <span className="tabular-nums text-slate-300">{p.nine_meter}</span>
                </div>
              ))}
              {r.nine_meter_shooters.length === 0 && <div className="text-sm text-slate-500">Nema 9m tagova (⇧4 na desk-u).</div>}
            </div>
          </div>
          <div className="card mt-4 p-4">
            <div className="text-[11px] uppercase text-slate-500 mb-2">Shot-map (poslednjih 5)</div>
            <ShotDots shots={r.shots} />
            <p className="mt-2 text-[12px] text-slate-500">{r.shots.length} tačaka sa court tap-a.</p>
          </div>
          <div className="card mt-4 p-4">
            <div className="text-[11px] uppercase text-slate-500 mb-2">Video cut-up</div>
            <p className="mb-2 text-[12px] text-slate-500">7m, 9m i 2 min protivnika iz poslednjih 5 — isti desk extracti.</p>
            {(r.clips ?? []).map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 py-1 text-sm">
                <span className="text-white">
                  {c.player || c.event_type}
                  <span className="ml-2 text-xs text-slate-500">{c.kind}{c.minute != null ? ` · ${c.minute}'` : ''}</span>
                </span>
                <span className="flex items-center gap-2">
                  {c.rating > 0 && <span className="tabular-nums text-slate-400">{c.rating.toFixed(1)}</span>}
                  {c.preview_url
                    ? <a href={c.preview_url} className="text-xs text-accent hover:underline" target="_blank" rel="noreferrer">Play</a>
                    : <Link to={`/clips`} className="text-xs text-slate-500">Desk</Link>}
                </span>
              </div>
            ))}
            {(r.clips ?? []).length === 0 && (
              <div className="text-sm text-slate-500">Nema objavljenih extracta za 7m / 9m / 2 min.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="card p-3">
      <div className="text-[11px] uppercase text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-white">{value}</div>
      {hint && <div className="text-[12px] text-slate-500">{hint}</div>}
    </div>
  );
}

function ShotDots({ shots }: { shots: { id: string; x?: number; y?: number; event_type: string }[] }) {
  return (
    <div className="relative h-56 w-full overflow-hidden rounded-lg bg-emerald-950/40 ring-1 ring-white/10">
      {shots.filter((s) => s.x != null && s.y != null).map((s) => (
        <span
          key={s.id}
          title={s.event_type}
          className={`absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full ${
            s.event_type.includes('goal') ? 'bg-amber-300' : 'bg-white/70'
          }`}
          style={{ left: `${s.x}%`, top: `${s.y}%` }}
        />
      ))}
      {shots.length === 0 && (
        <div className="grid h-full place-items-center text-sm text-slate-500">Nema koordinata.</div>
      )}
    </div>
  );
}
